/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import equal from "fast-deep-equal";
import { sorted } from "helpers";
import { useTenant } from "hooks/settings";
import { SyncedState, useSyncedState } from "hooks/state/syncing";
import { useMemo } from "react";
import { backendApi } from "src/store/api/enhancedApi";
import {
  SearchRead,
  SearchPeriod,
  useCreateSearchMutation,
} from "src/store/api/generatedApi";
import type { MaterialLimit } from "src/store/api/generatedApi";

export type SyncedParametersState = SyncedState<SearchRead>;

export const useSyncedParametersState = (
  clientSearchId: number | null,
  revision?: number,
  preSync?: () => void
): SyncedParametersState => {
  const tenant = useTenant();

  const [loadSearch] = backendApi.endpoints.getSearch.useLazyQuery();
  const [syncSearch] = useCreateSearchMutation();

  const source = useMemo(() => {
    if (clientSearchId == null) {
      return null;
    } else {
      return async () =>
        loadSearch({ tenantName: tenant, searchId: clientSearchId }).unwrap();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- revision is an intentional cache-buster: when it changes, source must be recreated so useSyncedState re-fetches
  }, [clientSearchId, revision, loadSearch, tenant]);

  return useSyncedState<SearchRead>(
    source,
    async (newSearch, parentSearch) => {
      if (preSync != null) {
        preSync();
      }
      return syncSearch({
        tenantName: tenant,
        searchCreate: {
          ...newSearch,
          parent_id: parentSearch.id,
        },
      }).unwrap();
    },
    searchParametersEqual
  );
};

export const searchParametersEqual = (
  left: SearchRead,
  right: SearchRead
): boolean =>
  equal(
    withoutNullFields(sortedParameters(left)),
    withoutNullFields(sortedParameters(right))
  );

// The API returns an optional field the client omitted as null.
const withoutNullFields = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(withoutNullFields);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, field]) => field != null)
        .map(([key, field]) => [key, withoutNullFields(field)])
    );
  }
  return value;
};

const sortedParameters = (parameters: SearchRead) => ({
  misc_params: parameters.misc_params,
  physical_parameters: parameters.physical_parameters,
  inventory: sorted(parameters.inventory, (item) => item.material_id),
  material_chemistry_overrides: sorted(
    parameters.material_chemistry_overrides,
    (item) => [item.material_id, item.chemical_element_id]
  ),
  fixed_mix_overrides: sorted(
    parameters.fixed_mix_overrides,
    (item) => item.steel_grade_name
  ),
  material_limit_overrides: sorted(
    parameters.material_limit_overrides.map(normalizedMaterialLimit),
    stableMaterialLimitSortKey
  ),
  periods: parameters.periods.map(sortedPeriod),
});

const normalizedMaterialLimit = (ml: MaterialLimit): MaterialLimit => ({
  ...ml,
  coefficients: sorted(ml.coefficients, (c) => c.material_constraint_class_id),
  bounds: sorted(ml.bounds, (b) => b.basket_id ?? -1),
});

// Use a deterministic composite key to avoid oscillating order when comparing
// client vs server states. If two overrides share the same `chef_group_id`, we
// still achieve a total ordering using name, hardness, coefficients and bounds.
const stableMaterialLimitSortKey = (ml: MaterialLimit): string => {
  const coeffs = [...ml.coefficients]
    .sort(
      (a, b) => a.material_constraint_class_id - b.material_constraint_class_id
    )
    .map(
      ({ material_constraint_class_id, coefficient }) =>
        `${material_constraint_class_id}:${coefficient.toFixed(3)}`
    )
    .join("|");

  const bounds = [...ml.bounds]
    .map((b) => ({
      basket_id: b.basket_id ?? -1,
      min: b.min_mass ?? 0,
      smin: b.soft_min_mass ?? 0,
      smax: b.soft_max_mass ?? 1000,
      max: b.max_mass ?? 1000,
    }))
    .sort((a, b) => {
      if (a.basket_id !== b.basket_id) {
        return a.basket_id - b.basket_id;
      }
      const a_sum = a.min + a.smin + a.smax + a.max;
      const b_sum = b.min + b.smin + b.smax + b.max;
      return a_sum - b_sum;
    })
    .map((b) => `${b.basket_id}:${b.min}:${b.smin}:${b.smax}:${b.max}`)
    .join("|");

  return `${ml.chef_group_id}::${ml.name ?? ""}::${
    ml.hardness
  }::${coeffs}::${bounds}`;
};

const sortedPeriod = (period: SearchPeriod) => ({
  name: period.name,
  start_timestamp:
    period.start_timestamp == null
      ? null
      : new Date(period.start_timestamp).toISOString(),
  end_timestamp:
    period.end_timestamp == null
      ? null
      : new Date(period.end_timestamp).toISOString(),
  product_groups: sorted(
    period.production_plan.product_group_items ?? [],
    (item) => item.product_group_id
  ),
  steel_grades: sorted(
    period.production_plan.steel_grade_items ?? [],
    (item) => item.steel_grade_id
  ),
  obtainable_bundles: sorted(period.obtainable_bundles, (item) => item.uuid),
  target_inventory: sorted(period.target_inventory, (item) => item.material_id),
  target_baskets: sorted(period.target_baskets, (item) => item.chef_group_id),
  material_exclusivity: sorted(period.material_exclusivity, (item) =>
    sorted(item.material_ids, (id) => id)
  ),
  suppress_mix_material_exclusivity_constraints:
    period.suppress_mix_material_exclusivity_constraints,
  suppress_min_feasible_mass_constraints:
    period.suppress_min_feasible_mass_constraints,
  optimisation_objective_weighting: period.optimisation_objective_weighting,
  electrical_energy_price: period.electrical_energy_price ?? 0,
  is_deployable: period.is_deployable,
});
